import React, { useCallback, useState, type ReactNode } from 'react';
import { JiraAtlassianNavigationSkeleton } from '@jira/navigation-apps__atlassian-navigation-skeleton/src/JiraAtlassianNavigationSkeleton.tsx';
import {
	TopNavigationBoundary,
	useTopNavigationSlots,
} from '@jira/navigation-apps__atlassian-navigation/src/ui/TopNavigation.tsx';
import type { useJiraAppChromeQueryData } from '@jira/nav-platform-integration__resources/src/useJiraAppChromeQuery.tsx';
import { ReportSSRFailOnUse } from '@jira/ssr__rendering-guard/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import Placeholder from '@atlassian/jira-placeholder';
import type { GlobalRootSlotProps, TopNavSlotExtraProps } from '@atlassian/global-root/types';
import type { GlobalTopNavigationProps } from '@atlassian/global-top-navigation/common/types';
import { stopInitialPageLoadTimingFromPerformanceMarkStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';

const TEST_ID = 'page-layout.top-nav' as const;

/**
 * The Relay fragment-ref this integration hands to Jira's top nav.
 *
 * Derived from the public signature of `useTopNavigationSlots` — the hook we
 * actually call — rather than re-importing the atlassian-navigation package's
 * generated `$key` type across the boundary. This keeps our data requirement
 * stated locally against the one public contract we consume, and leans into the
 * Relay compiler: the shape stays whatever `useTopNavigationSlots` masks.
 */
type TopNavSlotsQueryRef = Parameters<typeof useTopNavigationSlots>[0];

/**
 * Props for {@link NavPlatformTopNav}, matching the shape the platform
 * `GlobalRoot` passes to its `TopNavSlot`: the base `GlobalTopNavigation`
 * component (as `TopNavComponent`) plus the raw app-chrome `queryRef`.
 *
 * `queryRef` is intentionally omitted from the component props and provided as
 * the extra slot prop instead — it carries Jira's `...TopNavigation_slotsQuery`
 * fragment, unmasked here, rather than overriding `GlobalTopNavigation`'s own
 * data ref.
 */
export type NavPlatformTopNavProps = GlobalRootSlotProps<
	Omit<Partial<GlobalTopNavigationProps>, 'queryRef'>,
	'TopNavComponent',
	TopNavSlotExtraProps<useJiraAppChromeQueryData>
>;

/**
 * Jira's top-navigation integration for the platform `GlobalRoot`.
 *
 * Rendered into `GlobalRoot`'s `TopNavSlot`. It unmasks Jira's top-nav data
 * (spread as `...TopNavigation_slotsQuery` on the app-chrome query) off the raw
 * `queryRef`, then feeds Jira's app-home, start, middle, and end content into
 * the base `GlobalTopNavigation` via its `AppHomeSlot` and `UNSAFE__TopNav*Content`
 * props. The content is rendered under a Suspense boundary with a nav skeleton
 * fallback and an SSR failure guard, giving parity with the entry-point-driven
 * `AsyncAtlassianNavigation` path it replaces under the gate.
 *
 * When the data is not yet available or the `navx-global-top-nav-jira` gate is
 * off, the base top nav is rendered with its own default content.
 */
export function NavPlatformTopNav({
	TopNavComponent,
	queryRef,
}: NavPlatformTopNavProps): ReactNode {
	// GlobalRoot forwards the raw app-chrome query result here; because the slot
	// is typed over Jira's query data, it already satisfies `TopNavSlotsQueryRef`.
	const topNavContent: TopNavSlotsQueryRef | null = queryRef ?? null;

	if (topNavContent === null || !fg('navx-global-top-nav-jira')) {
		return <TopNavComponent />;
	}

	// Placeholder (a Suspense boundary with UFO Hold observability) + skeleton
	// fallback gives entry-point parity: if any part of the top-nav subtree
	// suspends (e.g. a lazily-loaded slot), the nav skeleton is shown, and
	// `ReportSSRFailOnUse` reports an SSR failure for the navigation page —
	// matching the entry-point-driven path GTN replaces under the gate.
	return (
		<Placeholder
			name="nav-platform-top-nav"
			fallback={
				<>
					<JiraAtlassianNavigationSkeleton />
					<ReportSSRFailOnUse
						pageId="navigation"
						fallbackId="NavPlatformTopNav"
						displayErrorInStaging
					/>
				</>
			}
		>
			<TopNavContent TopNavComponent={TopNavComponent} queryRefFragment={topNavContent} />
		</Placeholder>
	);
}

/**
 * Renders Jira's top-nav content into the base `GlobalTopNavigation`.
 *
 * Kept as its own component so it can sit under a Suspense boundary (see
 * {@link NavPlatformTopNav}) and so the initial-page-load timing marks fire once
 * when the real navigation content mounts — mirroring the entry-point-driven
 * `AsyncAtlassianNavigation` path that GTN replaces under the gate.
 */
function TopNavContent({
	TopNavComponent,
	queryRefFragment,
}: {
	TopNavComponent: NonNullable<NavPlatformTopNavProps['TopNavComponent']>;
	queryRefFragment: TopNavSlotsQueryRef;
}): ReactNode {
	// Emit the nav timing marks once, on the client, when the content first
	// renders. `stopInitialPageLoadTimingFromPerformanceMarkStart` is itself a
	// no-op on the server, so this is safe during SSR.
	useState(() => {
		stopInitialPageLoadTimingFromPerformanceMarkStart(
			'navigation-primary.bundle-ready',
			'jira-spa.js:eval-stop',
		);
		return undefined;
	});

	const { appHome, start, middle, end } = useTopNavigationSlots(queryRefFragment);

	// `AppHomeSlot` is a component *type*, so it needs a stable identity: an
	// inline `AppHomeSlot={() => appHome}` creates a new type every render, which
	// makes React unmount and remount the whole App Home subtree (discarding its
	// DOM state and focus). Memoise it so the identity only changes when the
	// resolved `appHome` content does.
	const AppHomeSlot = useCallback(() => appHome, [appHome]);

	// Note: This is not the final shape of the GTN integration, but an initial shape to get it into Jira.
	return (
		<TopNavigationBoundary>
			<TopNavComponent
				AppHomeSlot={AppHomeSlot}
				testId={TEST_ID}
				UNSAFE__TopNavStartContent={start}
				UNSAFE__TopNavMiddleContent={middle}
				UNSAFE__TopNavEndContent={end}
			/>
		</TopNavigationBoundary>
	);
}
